import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ShieldCheck,
  Users,
  Activity,
  Radio,
  ScrollText,
  Search,
  Loader2,
  X,
  Ban,
  KeyRound,
  LogOut,
  Coins,
  Crown,
  UserX,
  Copy,
  Check,
  RefreshCw,
  Circle,
} from 'lucide-react';
import { api } from '../../lib/api';
import { useSubPath } from '../../lib/useSubPath';
import { BRAND } from '../../brand';
import { Avatar } from '../Avatar';
import { DailyBars } from './DailyBars';

type Tab = 'overview' | 'users' | 'rooms' | 'log';

const TAB_SLUGS: Record<Tab, string> = { overview: 'resumen', users: 'usuarios', rooms: 'salas', log: 'registro' };

interface Point {
  day: string;
  value: number;
}

interface Overview {
  totals: {
    users: number;
    newThisWeek: number;
    activeToday: number;
    active7: number;
    active30: number;
    banned: number;
    admins: number;
    quizzes: number;
    guests: number;
    matches: number;
    online: number;
    rooms: number;
    playing: number;
  };
  signups: Point[];
  active: Point[];
  plays: Point[];
  log: { action: string; detail: string; created_at: string; admin: string | null; target: string | null }[];
}

interface AdminUser {
  id: string;
  username: string;
  displayName: string;
  avatar: unknown;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  birthDate: string | null;
  age: number | null;
  minor: boolean;
  country: string | null;
  countryName: string | null;
  createdAt: string;
  lastSeenAt: string | null;
  dRating: number;
  wins: number;
  totalGames: number;
  hostGames: number;
  accuracy: number;
  dahus: number;
  hasPassword: boolean;
  google: boolean;
  hasAiKey: boolean;
  aiCalls: number;
  termsAccepted: string | null;
  isAdmin: boolean;
  bannedAt: string | null;
  banReason: string | null;
  activeSessions: number;
  online: boolean;
}

interface AdminUserDetail extends AdminUser {
  inRoom: boolean;
  matches: { quiz_title: string; placement: number; total_players: number; score: number; rating_change: number; played_at: string; was_host: number }[];
  ledger: { delta: number; reason: string; ref: string | null; created_at: string }[];
  leagues: { name: string; role: string; status: string }[];
  quizzes: number;
  wallet: string | null;
  activeDays: number;
}

interface AdminRoom {
  pin: string;
  quizTitle: string;
  status: string;
  serverHosted: boolean;
  hostName: string;
  isPublic: boolean;
  questionNumber: number;
  totalQuestions: number;
  createdAt: number;
  players: { id: string; displayName: string; isGuest: boolean; isHost: boolean; connected: boolean; score: number }[];
}

const ACTION_LABELS: Record<string, string> = {
  ban: 'bloqueó a',
  unban: 'desbloqueó a',
  make_admin: 'hizo admin a',
  remove_admin: 'quitó el admin a',
  reset_password: 'reseteó la contraseña de',
  logout: 'cerró las sesiones de',
  dahus: 'ajustó los Dahus de',
  kick: 'sacó de una sala a',
  close_room: 'cerró una sala',
};

/** "hace 5 min", "hace 3 h", "hace 2 días"… o la fecha si es antigua. */
function ago(iso: string | null): string {
  if (!iso) return 'nunca';
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.round(ms / 60_000);
  if (min < 2) return 'ahora';
  if (min < 60) return `hace ${min} min`;
  const h = Math.round(min / 60);
  if (h < 24) return `hace ${h} h`;
  const d = Math.round(h / 24);
  if (d < 30) return `hace ${d} ${d === 1 ? 'día' : 'días'}`;
  return new Date(iso).toLocaleDateString('es', { day: 'numeric', month: 'short', year: 'numeric' });
}

/**
 * Panel de administración de la plataforma. Solo lo ve una cuenta con el rol
 * de admin; el servidor lo vuelve a comprobar en cada petición.
 */
export const AdminPanel: React.FC<{ myId: string; onOpenUser: (id: string) => void; notify: (m: string) => void }> = ({
  myId,
  onOpenUser,
  notify,
}) => {
  // Pestaña y usuario abierto van en la dirección (/admin/usuarios/<id>).
  const [[slug, userSeg], setSub] = useSubPath('/admin');
  const tab: Tab = (Object.keys(TAB_SLUGS) as Tab[]).find((t) => TAB_SLUGS[t] === slug) ?? 'overview';
  const openUser = tab === 'users' ? userSeg ?? null : null;
  const setTab = (t: Tab) => setSub(t === 'overview' ? [] : [TAB_SLUGS[t]], { keepScroll: true });
  const setOpenUser = (id: string | null, opts?: { replace?: boolean }) =>
    setSub(id ? [TAB_SLUGS.users, id] : [TAB_SLUGS.users], { keepScroll: true, ...opts });

  return (
    <div className="max-w-6xl mx-auto px-4 py-8 space-y-6">
      <div>
        <h1 className="text-2xl font-display font-bold flex items-center gap-2">
          <ShieldCheck className="w-6 h-6 text-ember-400" />
          Administración
        </h1>
        <p className="text-ink-400 text-sm mt-1">Usuarios, actividad y salas de toda la plataforma.</p>
      </div>

      <div role="tablist" className="flex gap-1 p-1 rounded-xl bg-ink-850 border border-ink-700 w-full sm:w-fit overflow-x-auto">
        <TabBtn active={tab === 'overview'} onClick={() => setTab('overview')} icon={<Activity className="w-4 h-4" />} label="Resumen" />
        <TabBtn active={tab === 'users'} onClick={() => setTab('users')} icon={<Users className="w-4 h-4" />} label="Usuarios" />
        <TabBtn active={tab === 'rooms'} onClick={() => setTab('rooms')} icon={<Radio className="w-4 h-4" />} label="Salas en vivo" />
        <TabBtn active={tab === 'log'} onClick={() => setTab('log')} icon={<ScrollText className="w-4 h-4" />} label="Registro" />
      </div>

      {tab === 'overview' && <OverviewTab />}
      {tab === 'users' && <UsersTab onOpen={setOpenUser} />}
      {tab === 'rooms' && <RoomsTab notify={notify} />}
      {tab === 'log' && <LogTab />}

      {openUser && (
        <UserDrawer
          id={openUser}
          myId={myId}
          onClose={() => setOpenUser(null)}
          onOpenProfile={(id) => {
            // Sin apilar: Atrás desde el perfil vuelve a la lista.
            setOpenUser(null, { replace: true });
            onOpenUser(id);
          }}
          notify={notify}
        />
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */

function useOverview() {
  const [data, setData] = useState<Overview | null>(null);
  const load = useCallback(async () => {
    try {
      setData(await api.get<Overview>('/admin/overview'));
    } catch {
      setData(null);
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);
  return { data, load };
}

const OverviewTab: React.FC = () => {
  const { data, load } = useOverview();
  if (!data) return <div className="h-40 shimmer rounded-xl" />;
  const t = data.totals;
  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <RefreshBtn onClick={() => void load()} />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Conectados ahora" value={t.online} sub={`${t.playing} jugando en ${t.rooms} ${t.rooms === 1 ? 'sala' : 'salas'}`} live />
        <Kpi label="Activos hoy" value={t.activeToday} sub={`${t.active7} esta semana · ${t.active30} en 30 días`} />
        <Kpi label="Cuentas" value={t.users} sub={`+${t.newThisWeek} esta semana`} />
        <Kpi label="Partidas jugadas" value={t.matches} sub={`${t.quizzes} trivias · ${t.guests} invitados`} />
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        <DailyBars title="Usuarios activos por día" data={data.active} unit="usuarios" />
        <DailyBars title="Cuentas nuevas por día" data={data.signups} unit="cuentas" />
        <DailyBars title="Partidas jugadas por día (cada jugador cuenta una)" data={data.plays} unit="participaciones" />
      </div>
      <p className="text-xs text-ink-500">
        Los usuarios activos se cuentan desde que existe este panel: los días anteriores salen a cero.
        {t.banned > 0 && ` · ${t.banned} ${t.banned === 1 ? 'cuenta bloqueada' : 'cuentas bloqueadas'}.`}
        {` · ${t.admins} ${t.admins === 1 ? 'admin' : 'admins'}.`}
      </p>
    </div>
  );
};

const Kpi: React.FC<{ label: string; value: number; sub: string; live?: boolean }> = ({ label, value, sub, live }) => (
  <div className="surface p-4">
    <p className="text-ink-500 text-xs flex items-center gap-1.5">
      {live && <span className="w-2 h-2 rounded-full bg-success-500 animate-pulse" aria-hidden="true" />}
      {label}
    </p>
    <p className="text-3xl font-display font-black tabular-nums text-ink-50 mt-1">{value.toLocaleString('es')}</p>
    <p className="text-ink-500 text-xs mt-0.5">{sub}</p>
  </div>
);

/* ------------------------------------------------------------------ */

type SortKey = 'lastSeen' | 'created' | 'name' | 'games' | 'rating';

const UsersTab: React.FC<{ onOpen: (id: string) => void }> = ({ onOpen }) => {
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [q, setQ] = useState('');
  const [sort, setSort] = useState<SortKey>('lastSeen');
  const [only, setOnly] = useState<'all' | 'online' | 'banned' | 'admins'>('all');

  const load = useCallback(async () => {
    try {
      const res = await api.get<{ users: AdminUser[] }>('/admin/users');
      setUsers(res.users);
    } catch {
      setUsers([]);
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  const visible = useMemo(() => {
    if (!users) return [];
    const needle = q.trim().toLowerCase();
    const list = users.filter((u) => {
      if (only === 'online' && !u.online) return false;
      if (only === 'banned' && !u.bannedAt) return false;
      if (only === 'admins' && !u.isAdmin) return false;
      if (!needle) return true;
      return [u.username, u.email, u.firstName, u.lastName, u.countryName]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(needle));
    });
    const time = (s: string | null) => (s ? new Date(s).getTime() : 0);
    return list.sort((a, b) =>
      sort === 'name'
        ? a.username.localeCompare(b.username)
        : sort === 'created'
          ? time(b.createdAt) - time(a.createdAt)
          : sort === 'games'
            ? b.totalGames - a.totalGames
            : sort === 'rating'
              ? b.dRating - a.dRating
              : Number(b.online) - Number(a.online) || time(b.lastSeenAt) - time(a.lastSeenAt),
    );
  }, [users, q, sort, only]);

  if (!users) return <div className="h-40 shimmer rounded-xl" />;

  return (
    <div className="space-y-3">
      <div className="flex gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[14rem]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-500 pointer-events-none" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Usuario, correo, nombre o país…"
            aria-label="Buscar usuarios"
            type="search"
            className="w-full h-10 pl-9 pr-3 rounded-lg bg-ink-850 border border-ink-700 text-ink-100 text-sm outline-none focus:border-sky-500"
          />
        </div>
        <select value={only} onChange={(e) => setOnly(e.target.value as typeof only)} aria-label="Filtrar" className="h-10 px-2 rounded-lg bg-ink-850 border border-ink-700 text-ink-200 text-sm">
          <option value="all">Todos ({users.length})</option>
          <option value="online">Conectados ({users.filter((u) => u.online).length})</option>
          <option value="banned">Bloqueados ({users.filter((u) => u.bannedAt).length})</option>
          <option value="admins">Admins ({users.filter((u) => u.isAdmin).length})</option>
        </select>
        <select value={sort} onChange={(e) => setSort(e.target.value as SortKey)} aria-label="Ordenar" className="h-10 px-2 rounded-lg bg-ink-850 border border-ink-700 text-ink-200 text-sm">
          <option value="lastSeen">Última conexión</option>
          <option value="created">Más nuevas</option>
          <option value="name">Nombre</option>
          <option value="games">Partidas</option>
          <option value="rating">D-Rating</option>
        </select>
        <RefreshBtn onClick={() => void load()} />
      </div>

      <div className="surface overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-ink-500 border-b border-ink-800">
            <tr>
              <th className="px-3 py-2 font-medium">Usuario</th>
              <th className="px-3 py-2 font-medium hidden md:table-cell">Correo</th>
              <th className="px-3 py-2 font-medium hidden lg:table-cell">País · edad</th>
              <th className="px-3 py-2 font-medium">Última conexión</th>
              <th className="px-3 py-2 font-medium hidden sm:table-cell text-right">Partidas</th>
              <th className="px-3 py-2 font-medium hidden sm:table-cell text-right">Alta</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-800">
            {visible.map((u) => (
              <tr key={u.id} onClick={() => onOpen(u.id)} className="cursor-pointer hover:bg-ink-850/70">
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="relative shrink-0">
                      <Avatar config={u.avatar} size={30} className="shadow-none" />
                      {u.online && <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-success-500 border-2 border-ink-900" />}
                    </span>
                    <span className="truncate font-medium text-ink-100">{u.username}</span>
                    {u.isAdmin && <Crown className="w-3.5 h-3.5 text-ember-400 shrink-0" aria-label="Admin" />}
                    {u.bannedAt && <span className="chip text-[10px] border-danger-500 text-danger-400 bg-danger-500/10 shrink-0">Bloqueado</span>}
                  </div>
                </td>
                <td className="px-3 py-2 text-ink-400 hidden md:table-cell truncate max-w-[14rem]">{u.email ?? '—'}</td>
                <td className="px-3 py-2 text-ink-400 hidden lg:table-cell">
                  {[u.countryName, u.age !== null ? `${u.age}` : null].filter(Boolean).join(' · ') || '—'}
                </td>
                <td className={`px-3 py-2 whitespace-nowrap ${u.online ? 'text-success-400 font-medium' : 'text-ink-400'}`}>
                  {u.online ? 'Conectado' : ago(u.lastSeenAt)}
                </td>
                <td className="px-3 py-2 text-right tabular-nums hidden sm:table-cell text-ink-300">{u.totalGames}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap hidden sm:table-cell text-ink-500 text-xs">
                  {new Date(u.createdAt).toLocaleDateString('es', { day: 'numeric', month: 'short', year: '2-digit' })}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {visible.length === 0 && <p className="text-center text-ink-500 text-sm py-8">Nadie coincide.</p>}
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */

const UserDrawer: React.FC<{
  id: string;
  myId: string;
  onClose: () => void;
  onOpenProfile: (id: string) => void;
  notify: (m: string) => void;
}> = ({ id, myId, onClose, onOpenProfile, notify }) => {
  const [u, setU] = useState<AdminUserDetail | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [tempPassword, setTempPassword] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [banReason, setBanReason] = useState('');
  const [dahus, setDahus] = useState('');
  const [dahusNote, setDahusNote] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await api.get<{ user: AdminUserDetail }>(`/admin/users/${id}`);
      setU(res.user);
    } catch (err) {
      notify(err instanceof Error ? err.message : 'No se pudo cargar.');
      onClose();
    }
  }, [id, notify, onClose]);

  useEffect(() => {
    void load();
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [load, onClose]);

  const act = async (key: string, run: () => Promise<unknown>, done: string) => {
    setBusy(key);
    try {
      await run();
      notify(done);
      await load();
    } catch (err) {
      notify(err instanceof Error ? err.message : 'No se pudo completar.');
    } finally {
      setBusy(null);
    }
  };

  const self = id === myId;

  return (
    <div className="fixed inset-0 z-50 bg-ink-950/70 backdrop-blur-sm flex justify-end" onMouseDown={(e) => e.target === e.currentTarget && onClose()} role="dialog" aria-modal="true" aria-label="Ficha del usuario">
      <div className="w-full max-w-lg h-full overflow-y-auto bg-ink-900 border-l border-ink-700 shadow-2xl animate-rise">
        {!u ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-sky-500" />
          </div>
        ) : (
          <div className="p-5 space-y-5">
            <div className="flex items-start gap-3">
              <Avatar config={u.avatar} size={64} />
              <div className="flex-1 min-w-0">
                <h2 className="text-xl font-display font-bold text-ink-50 truncate flex items-center gap-2">
                  {u.username}
                  {u.isAdmin && <Crown className="w-4 h-4 text-ember-400" />}
                </h2>
                <p className={`text-sm ${u.online ? 'text-success-400' : 'text-ink-500'} flex items-center gap-1.5`}>
                  <Circle className={`w-2.5 h-2.5 ${u.online ? 'fill-success-500 text-success-500' : 'fill-ink-600 text-ink-600'}`} />
                  {u.online ? (u.inRoom ? 'Conectado, en una sala' : 'Conectado') : `Última conexión: ${ago(u.lastSeenAt)}`}
                </p>
                {u.bannedAt && (
                  <p className="text-sm text-danger-400 mt-1">
                    Bloqueado {ago(u.bannedAt)}
                    {u.banReason && `: ${u.banReason}`}
                  </p>
                )}
              </div>
              <button onClick={onClose} className="p-2 rounded-lg text-ink-500 hover:text-ink-100 hover:bg-ink-800" aria-label="Cerrar">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Datos */}
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2.5 text-sm">
              <Field label="Nombre real" value={[u.firstName, u.lastName].filter(Boolean).join(' ') || '—'} />
              <Field label="Correo" value={u.email ?? '—'} />
              <Field label="Nacimiento" value={u.birthDate ? `${u.birthDate} (${u.age} años)${u.minor ? ' · menor' : ''}` : '—'} />
              <Field label="País" value={u.countryName ?? '—'} />
              <Field label="Alta" value={new Date(u.createdAt).toLocaleString('es')} />
              <Field label="Días con actividad" value={`${u.activeDays}`} />
              <Field label="Acceso" value={[u.hasPassword && 'contraseña', u.google && 'Google'].filter(Boolean).join(' + ') || '—'} />
              <Field label="Sesiones abiertas" value={`${u.activeSessions}`} />
              <Field label="D-Rating" value={`${u.dRating}`} />
              <Field label="Partidas" value={`${u.totalGames} (${u.wins} ganadas) · ${u.hostGames} como anfitrión`} />
              <Field label="Aciertos" value={`${Math.round(u.accuracy * 100)}%`} />
              <Field label={BRAND.currencyName} value={u.dahus.toLocaleString('es')} />
              <Field label="Trivias creadas" value={`${u.quizzes}`} />
              <Field label="IA" value={u.hasAiKey ? `clave propia · ${u.aiCalls} usos` : `sin clave · ${u.aiCalls} usos`} />
              <Field label="Billetera" value={u.wallet ? `${u.wallet.slice(0, 6)}…${u.wallet.slice(-4)}` : '—'} />
              <Field label="Ligas" value={u.leagues.map((l) => `${l.name}${l.role === 'admin' ? ' (admin)' : ''}${l.status !== 'active' ? ' (pendiente)' : ''}`).join(', ') || '—'} />
            </dl>

            <button onClick={() => onOpenProfile(u.id)} className="text-sm text-sky-400 hover:text-sky-300 font-medium">
              Ver su perfil público
            </button>

            {/* Contraseña temporal recién generada */}
            {tempPassword && (
              <div className="rounded-xl border border-ember-500 bg-ember-500/10 p-4 space-y-2">
                <p className="text-sm text-ink-100 font-semibold">Contraseña temporal</p>
                <p className="text-xs text-ink-400">
                  Pásasela a {u.username} por un canal privado. Solo se muestra esta vez; sus sesiones se han cerrado.
                </p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 font-mono text-lg tracking-wider text-ink-50 bg-ink-850 rounded-lg px-3 py-2 select-all">{tempPassword}</code>
                  <button
                    onClick={() => {
                      void navigator.clipboard.writeText(tempPassword).then(() => {
                        setCopied(true);
                        setTimeout(() => setCopied(false), 1500);
                      });
                    }}
                    className="p-2.5 rounded-lg bg-ink-800 hover:bg-ink-700 text-ink-100"
                    aria-label="Copiar contraseña"
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            )}

            {/* Acciones */}
            <section className="space-y-3">
              <h3 className="text-xs uppercase tracking-wider text-ink-500 font-semibold">Herramientas</h3>

              <div className="grid grid-cols-2 gap-2">
                <ActionBtn
                  icon={<KeyRound className="w-4 h-4" />}
                  label="Resetear contraseña"
                  busy={busy === 'pw'}
                  onClick={async () => {
                    if (!window.confirm(`¿Poner una contraseña temporal a ${u.username}? Sus sesiones se cerrarán.`)) return;
                    setBusy('pw');
                    try {
                      const res = await api.post<{ password: string }>(`/admin/users/${u.id}/reset-password`);
                      setTempPassword(res.password);
                      await load();
                    } catch (err) {
                      notify(err instanceof Error ? err.message : 'No se pudo resetear.');
                    } finally {
                      setBusy(null);
                    }
                  }}
                />
                <ActionBtn
                  icon={<LogOut className="w-4 h-4" />}
                  label="Cerrar sus sesiones"
                  busy={busy === 'out'}
                  disabled={self}
                  onClick={() => void act('out', () => api.post(`/admin/users/${u.id}/logout`), 'Sesiones cerradas.')}
                />
                <ActionBtn
                  icon={<Crown className="w-4 h-4" />}
                  label={u.isAdmin ? 'Quitar admin' : 'Hacer admin'}
                  busy={busy === 'admin'}
                  disabled={self}
                  onClick={() => {
                    if (!u.isAdmin && !window.confirm(`¿Dar a ${u.username} acceso total a este panel?`)) return;
                    void act('admin', () => api.put(`/admin/users/${u.id}/admin`, { on: !u.isAdmin }), u.isAdmin ? 'Ya no es admin.' : 'Ahora es admin.');
                  }}
                />
                {u.bannedAt ? (
                  <ActionBtn
                    icon={<UserX className="w-4 h-4" />}
                    label="Desbloquear"
                    busy={busy === 'unban'}
                    onClick={() => void act('unban', () => api.delete(`/admin/users/${u.id}/ban`), 'Cuenta desbloqueada.')}
                  />
                ) : null}
              </div>

              {!u.bannedAt && !self && (
                <div className="rounded-xl border border-danger-500/50 p-3 space-y-2">
                  <p className="text-sm font-semibold text-danger-400 flex items-center gap-1.5">
                    <Ban className="w-4 h-4" /> Bloquear cuenta
                  </p>
                  <input
                    value={banReason}
                    onChange={(e) => setBanReason(e.target.value)}
                    maxLength={200}
                    placeholder="Motivo (lo verá al intentar entrar)"
                    className="w-full h-10 px-3 rounded-lg bg-ink-850 border border-ink-700 text-ink-100 text-sm outline-none focus:border-danger-500"
                  />
                  <button
                    onClick={() => {
                      if (!window.confirm(`¿Bloquear a ${u.username}? Se le cerrará todo y no podrá entrar.`)) return;
                      void act('ban', () => api.post(`/admin/users/${u.id}/ban`, { reason: banReason }), `${u.username} bloqueado.`);
                    }}
                    disabled={busy === 'ban'}
                    className="w-full h-10 rounded-lg text-sm font-semibold bg-danger-500 hover:bg-danger-400 text-white disabled:opacity-50"
                  >
                    Bloquear
                  </button>
                </div>
              )}

              <div className="rounded-xl border border-ink-700 p-3 space-y-2">
                <p className="text-sm font-semibold text-ink-200 flex items-center gap-1.5">
                  <Coins className="w-4 h-4 text-ember-400" /> Ajustar {BRAND.currencyName}
                </p>
                <div className="flex gap-2">
                  <input
                    value={dahus}
                    onChange={(e) => setDahus(e.target.value.replace(/[^\d-]/g, ''))}
                    inputMode="numeric"
                    placeholder="+100 o -50"
                    aria-label="Cantidad"
                    className="w-28 h-10 px-3 rounded-lg bg-ink-850 border border-ink-700 text-ink-100 text-sm outline-none focus:border-sky-500"
                  />
                  <input
                    value={dahusNote}
                    onChange={(e) => setDahusNote(e.target.value)}
                    maxLength={120}
                    placeholder="Motivo"
                    aria-label="Motivo del ajuste"
                    className="flex-1 min-w-0 h-10 px-3 rounded-lg bg-ink-850 border border-ink-700 text-ink-100 text-sm outline-none focus:border-sky-500"
                  />
                  <button
                    onClick={() =>
                      void act('dahus', () => api.post(`/admin/users/${u.id}/dahus`, { amount: Number(dahus), note: dahusNote }), 'Saldo ajustado.').then(() => {
                        setDahus('');
                        setDahusNote('');
                      })
                    }
                    disabled={!dahus || dahus === '-' || busy === 'dahus'}
                    className="px-3 h-10 rounded-lg text-sm font-semibold bg-ember-500 hover:bg-ember-400 text-on-accent disabled:opacity-50"
                  >
                    Aplicar
                  </button>
                </div>
              </div>
            </section>

            {/* Historial */}
            <section className="space-y-2">
              <h3 className="text-xs uppercase tracking-wider text-ink-500 font-semibold">Últimas partidas</h3>
              {u.matches.length === 0 ? (
                <p className="text-sm text-ink-500">Sin partidas.</p>
              ) : (
                <ul className="text-sm divide-y divide-ink-800">
                  {u.matches.map((m, i) => (
                    <li key={i} className="py-1.5 flex gap-2">
                      <span className="flex-1 min-w-0 truncate text-ink-200">{m.quiz_title}</span>
                      <span className="text-ink-500 tabular-nums shrink-0">
                        {m.placement > 0 ? `${m.placement}º de ${m.total_players}` : 'anfitrión'}
                      </span>
                      <span className="text-ink-500 text-xs shrink-0 w-20 text-right">{ago(m.played_at)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="space-y-2">
              <h3 className="text-xs uppercase tracking-wider text-ink-500 font-semibold">Movimientos de {BRAND.currencyName}</h3>
              {u.ledger.length === 0 ? (
                <p className="text-sm text-ink-500">Sin movimientos.</p>
              ) : (
                <ul className="text-sm divide-y divide-ink-800">
                  {u.ledger.map((l, i) => (
                    <li key={i} className="py-1.5 flex gap-2">
                      <span className={`tabular-nums w-16 shrink-0 font-semibold ${l.delta >= 0 ? 'text-success-400' : 'text-danger-400'}`}>
                        {l.delta > 0 ? '+' : ''}
                        {l.delta}
                      </span>
                      <span className="flex-1 min-w-0 truncate text-ink-400">{l.reason}{l.ref ? ` · ${l.ref}` : ''}</span>
                      <span className="text-ink-500 text-xs shrink-0">{ago(l.created_at)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
};

const Field: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="min-w-0">
    <dt className="text-xs text-ink-500">{label}</dt>
    <dd className="text-ink-100 break-words">{value}</dd>
  </div>
);

const ActionBtn: React.FC<{
  icon: React.ReactNode;
  label: string;
  busy: boolean;
  disabled?: boolean;
  onClick: () => void;
}> = ({ icon, label, busy, disabled, onClick }) => (
  <button
    onClick={onClick}
    disabled={busy || disabled}
    className="flex items-center justify-center gap-1.5 h-10 rounded-lg text-sm font-semibold border border-ink-700 bg-ink-850 text-ink-100 hover:bg-ink-800 disabled:opacity-40 transition-colors"
  >
    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : icon}
    {label}
  </button>
);

/* ------------------------------------------------------------------ */

const STATUS_LABELS: Record<string, string> = {
  lobby: 'Esperando',
  countdown: 'Empezando',
  question: 'Pregunta',
  reveal: 'Revisando',
  podium: 'Podio',
};

const RoomsTab: React.FC<{ notify: (m: string) => void }> = ({ notify }) => {
  const [rooms, setRooms] = useState<AdminRoom[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get<{ rooms: AdminRoom[] }>('/admin/rooms');
      setRooms(res.rooms);
    } catch {
      setRooms([]);
    }
  }, []);
  useEffect(() => {
    void load();
    const t = window.setInterval(() => void load(), 5000);
    return () => window.clearInterval(t);
  }, [load]);

  const act = async (key: string, run: () => Promise<unknown>, done: string) => {
    setBusy(key);
    try {
      await run();
      notify(done);
      await load();
    } catch (err) {
      notify(err instanceof Error ? err.message : 'No se pudo completar.');
    } finally {
      setBusy(null);
    }
  };

  if (!rooms) return <div className="h-40 shimmer rounded-xl" />;
  if (rooms.length === 0) return <div className="surface p-10 text-center text-ink-400 text-sm">No hay ninguna sala abierta ahora.</div>;

  return (
    <div className="space-y-3">
      <p className="text-xs text-ink-500">Se actualiza solo cada 5 segundos.</p>
      {rooms.map((r) => (
        <div key={r.pin} className="surface p-4 space-y-3">
          <div className="flex items-start gap-3 flex-wrap">
            <div className="flex-1 min-w-[12rem]">
              <p className="font-semibold text-ink-50">
                {r.quizTitle} <span className="font-mono text-ink-500 text-sm">· {r.pin}</span>
              </p>
              <p className="text-xs text-ink-500">
                {r.hostName} · {STATUS_LABELS[r.status] ?? r.status}
                {r.questionNumber > 0 && ` ${r.questionNumber}/${r.totalQuestions}`} · {r.isPublic ? 'pública' : 'privada'} · abierta {ago(new Date(r.createdAt).toISOString())}
              </p>
            </div>
            <button
              onClick={() => {
                if (window.confirm(`¿Cerrar la sala ${r.pin} para todos?`)) void act(`close-${r.pin}`, () => api.post(`/admin/rooms/${r.pin}/close`), 'Sala cerrada.');
              }}
              disabled={busy === `close-${r.pin}`}
              className="px-3 h-9 rounded-lg text-sm font-semibold text-danger-400 border border-danger-500/50 hover:bg-danger-500/10 disabled:opacity-50"
            >
              Cerrar sala
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {r.players.map((p) => (
              <span key={p.id} className={`flex items-center gap-1.5 pl-2.5 pr-1 py-1 rounded-full border text-xs ${p.connected ? 'border-ink-700 bg-ink-850 text-ink-200' : 'border-ink-800 text-ink-500'}`}>
                {p.isHost && <Crown className="w-3 h-3 text-ember-400" />}
                {p.displayName}
                {p.isGuest && <span className="text-ink-500">(invitado)</span>}
                {!p.isHost ? (
                  <button
                    onClick={() => {
                      if (window.confirm(`¿Sacar a ${p.displayName} de la sala ${r.pin}?`)) void act(`k-${p.id}`, () => api.post(`/admin/rooms/${r.pin}/kick`, { playerId: p.id }), `${p.displayName} fuera de la sala.`);
                    }}
                    className="p-1 rounded-full hover:bg-danger-500/15 hover:text-danger-400"
                    aria-label={`Sacar a ${p.displayName}`}
                    title="Sacar de la sala"
                  >
                    <X className="w-3 h-3" />
                  </button>
                ) : (
                  <span className="w-1" />
                )}
              </span>
            ))}
            {r.players.length === 0 && <span className="text-xs text-ink-500">Sin nadie dentro.</span>}
          </div>
        </div>
      ))}
    </div>
  );
};

/* ------------------------------------------------------------------ */

const LogTab: React.FC = () => {
  const { data } = useOverview();
  if (!data) return <div className="h-40 shimmer rounded-xl" />;
  if (data.log.length === 0) return <div className="surface p-10 text-center text-ink-400 text-sm">Todavía no hay acciones registradas.</div>;
  return (
    <ul className="surface divide-y divide-ink-800 text-sm">
      {data.log.map((l, i) => (
        <li key={i} className="px-4 py-2.5 flex gap-3">
          <span className="flex-1 min-w-0 text-ink-200">
            <span className="font-semibold">{l.admin ?? '¿?'}</span> {ACTION_LABELS[l.action] ?? l.action}
            {l.target && <span className="font-semibold"> {l.target}</span>}
            {l.detail && <span className="text-ink-500"> · {l.detail}</span>}
          </span>
          <span className="text-ink-500 text-xs shrink-0">{ago(l.created_at)}</span>
        </li>
      ))}
    </ul>
  );
};

/* ------------------------------------------------------------------ */

const TabBtn: React.FC<{ active: boolean; onClick: () => void; icon: React.ReactNode; label: string }> = ({ active, onClick, icon, label }) => (
  <button
    role="tab"
    aria-selected={active}
    onClick={onClick}
    className={`flex items-center gap-1.5 px-3 h-9 rounded-lg text-sm font-semibold whitespace-nowrap transition-colors ${
      active ? 'bg-ink-700 text-ink-50' : 'text-ink-400 hover:text-ink-100'
    }`}
  >
    {icon}
    {label}
  </button>
);

const RefreshBtn: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <button onClick={onClick} className="flex items-center gap-1.5 px-3 h-10 rounded-lg text-sm font-medium border border-ink-700 bg-ink-850 text-ink-300 hover:text-ink-100" aria-label="Actualizar">
    <RefreshCw className="w-4 h-4" />
    <span className="hidden sm:inline">Actualizar</span>
  </button>
);
